import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../../api/store";
import { PolicyInformationState } from "./policy-information.state";
import { participationRequirementsInitialState, underwritingProvisionsInitialState } from "./policy-information.initial_state";
import { AllowedNextStatus } from "../../../../api/ebam/policy/policy-response";

export type policyInformationType = 
    null 
    | "eligibleIndividuals" 
    | "contribution" 
    | "participationRequirements" 
    | "underwritingProvisions" 
    | "underwritingProvisionsNotes" 
    | 'cityMunicipalityProvince'
    | 'specialUnderwritingProvisions' // gaddp
    | 'docCode'

export type NormalFieldsType = 
    "eligibleIndividuals" 
    | "contribution" 
    | "underwritingProvisionsNotes" 
    | "cityMunicipalityProvince" 
    | "specialUnderwritingProvisions" 
    | "docCode"

const initialState: PolicyInformationState = {
    policyInformationType: null,
    eligibleIndividuals: "",
    contribution: "",
    underwritingProvisions: underwritingProvisionsInitialState,
    participationRequirements: participationRequirementsInitialState,
    underwritingProvisionsNotes: "",

    cityMunicipalityProvince: "",
    docCode: "",
    allowedNextStatus: null,

    specialUnderwritingProvisions: "",

    // effectiveData: '',
    effectiveDate: {
        isEdit: false,
        value: ''
    }
};

const policyInformationSlice = createSlice({
    name: "policyInformationSlice",
    initialState,
    reducers: {
        setEffectiveDate: (state, action: PayloadAction<Partial<{ isEdit: boolean; value: string }>>) => {
            Object.assign(state.effectiveDate, action.payload);
        },
                // For normal input fields
        onChangePolicyInformation: ( state, action: PayloadAction<{ field: keyof Pick<PolicyInformationState, NormalFieldsType>; value: string; }>) => {
            const { field, value } = action.payload;
            state[field] = value;
        },
        // For selecting policy information type
        setPolicyInformationType: ( state, action: PayloadAction<PolicyInformationState["policyInformationType"]> ) => {
            state.policyInformationType = action.payload;
        },
        // Update underwriting provision
        onChangeUnderwritingProvision: ( state, action: PayloadAction<{ id: number; field: "amount" | "ageRange" | "description"; value: number | string; }> ) => {
            const { id, field, value } = action.payload;
            const provision = state.underwritingProvisions.find((item) => item.id === id);
            if (!provision) return;

            if (field === "amount") {
                provision.amount = value === "" ? undefined : Number(value);
            } else if (field === "ageRange") {
                provision.ageRange = String(value);
            } else if (field === "description") {
                provision.description = String(value);
            }
        },
        // Update participation requirement amount
        onChangeParticipationRequirement: ( state, action: PayloadAction<{ id: number; amount: number | string; }> ) => {
            const { id, amount } = action.payload;

            const requirement = state.participationRequirements.find((item) => item.id === id);
            if (!requirement) return;

            if (id === 1) {
                requirement.amount = amount === "" ? "" : Number(amount);
            } else {
                requirement.amount = String(amount);
            }
        },

        setAllowedNextStatus: (state, action: PayloadAction<null | AllowedNextStatus[]>) => {
            state.allowedNextStatus = action.payload;
        },

        resetPolicyInformation: () => {
            return structuredClone(initialState);
        },
    },
});

export const {
    setEffectiveDate,
    onChangePolicyInformation,
    setPolicyInformationType,
    onChangeUnderwritingProvision,
    onChangeParticipationRequirement,
    setAllowedNextStatus,
    resetPolicyInformation
} = policyInformationSlice.actions;

export const selectPolicyInformationType = (state: RootState) => state.policyInformationSlice.policyInformationType;
export const selectPolicyEligibleIndividuals = (state: RootState) => state.policyInformationSlice.eligibleIndividuals;
export const selectPolicyContribution = (state: RootState) => state.policyInformationSlice.contribution;
export const selectPolicyParticipationRequirements = (state: RootState) => state.policyInformationSlice.participationRequirements;
export const selectPolicyUnderwritingProvisions = (state: RootState) => state.policyInformationSlice.underwritingProvisions;
export const selectPolicyUnderwritingProvisionsNotes = (state: RootState) => state.policyInformationSlice.underwritingProvisionsNotes;

export const selectPolicyCityMunicipalityProvince = (state: RootState) => state.policyInformationSlice.cityMunicipalityProvince;
export const selectPolicyDocumentCode = (state: RootState) => state.policyInformationSlice.docCode;
export const selectPolicyAllowedNextStatus = (state: RootState) => state.policyInformationSlice.allowedNextStatus;

export const selectSpecialUnderwritingProvision = (state: RootState) => state.policyInformationSlice.specialUnderwritingProvisions;
export const selectPolicyEffectiveDate = (state: RootState) => state.policyInformationSlice.effectiveDate;

export default policyInformationSlice.reducer;